import React, { useState } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useLang } from "../context/LangContext";
import { pathFor, keyForPath } from "../lib/routes";
import { BRAND_INFO } from "../lib/content";
import { Menu, X, Instagram } from "lucide-react";

export default function Navbar() {
  const { lang, setLang, t } = useLang();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const switchLang = (newLang) => {
    if (newLang === lang) return;
    const key = keyForPath(location.pathname);
    setLang(newLang);
    if (key) {
      const next = pathFor(key, newLang);
      if (next && next !== location.pathname) navigate(next);
    }
  };

  const p = (k) => pathFor(k, lang);

  const links = [
    { to: p("treatments"), label: t("nav.treatments"), key: "treatments" },
    { to: p("yacht"), label: t("nav.yacht"), key: "yacht" },
    { to: p("villa"), label: t("nav.villa"), key: "villa" },
    { to: p("xpTherapy"), label: t("nav.xp"), key: "xpTherapy" },
    { to: p("exomobil"), label: t("nav.exomobil"), key: "exomobil" },
    { to: p("partners"), label: t("nav.partners"), key: "partners" },
  ];

  return (
    <header className="fixed top-0 inset-x-0 z-50 glass-nav" data-testid="navbar">
      <div className="container-luxe flex items-center justify-between h-16">
        <Link to={p("home")} className="flex items-center gap-3 shrink-0" data-testid="nav-logo">
          <img
            src="/logos/riviera-shockwaves@4x.png"
            srcSet="/logos/riviera-shockwaves.png 300w, /logos/riviera-shockwaves@4x.png 1200w"
            sizes="(max-width: 768px) 157px, 172px"
            width="300"
            height="84"
            alt="Riviera Shockwaves"
            className="block h-11 md:h-12 w-auto shrink-0 rounded-sm opacity-95 hover:opacity-100 transition"
          />
        </Link>
        <nav className="hidden xl:flex items-center gap-5">
          {links.map((l) => (
            <NavLink
              key={l.key}
              to={l.to}
              data-testid={`nav-link-${l.key}`}
              className={({ isActive }) =>
                `text-[11px] tracking-[0.16em] uppercase font-display font-medium transition-colors ${
                  isActive ? "text-[var(--gold)]" : "text-[var(--ink)] hover:text-[var(--gold)]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          {/* Instagram — gros bouton doré */}
          <a
            href={BRAND_INFO.instagram}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Instagram Riviera Shockwaves"
            data-testid="nav-instagram"
            className="hidden lg:inline-flex items-center gap-2 h-10 px-3.5 rounded-full bg-[var(--gold)] text-white hover:bg-[var(--ink)] transition-colors"
          >
            <Instagram size={16} />
            <span className="font-display text-[11px] font-semibold uppercase tracking-[0.16em]">Instagram</span>
          </a>
          {/* Desktop lang switch */}
          <div className="hidden sm:flex items-center text-[11px] font-display border border-[var(--ink)]/15 rounded-full overflow-hidden">
            {["fr", "en", "it", "ru"].map((code) => (
              <button
                key={code}
                data-testid={`lang-${code}`}
                onClick={() => switchLang(code)}
                className={`px-1.5 py-1.5 ${lang === code ? "bg-[var(--ink)] text-[var(--bg-base)]" : "text-[var(--ink)]/60 hover:text-[var(--ink)]"}`}
              >{code.toUpperCase()}</button>
            ))}
          </div>
          {/* Mobile compact lang indicator (also opens menu) */}
          <button
            data-testid="lang-mobile-current"
            onClick={() => setOpen(true)}
            className="sm:hidden text-[11px] font-display tracking-[0.15em] uppercase border border-[var(--ink)]/20 rounded-full px-3 py-1.5 text-[var(--ink)]"
            aria-label="Change language"
          >{lang.toUpperCase()}</button>
          <div className="hidden md:block shrink-0">
            <Link to={p("booking")} className="btn-gold whitespace-nowrap" data-testid="nav-cta-book">
              {t("cta.book_vip")}
            </Link>
          </div>
          <button
            data-testid="nav-mobile-toggle"
            className="xl:hidden p-2 text-[var(--ink)]"
            onClick={() => setOpen(!open)}
            aria-label="menu"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>
      {open && (
        <div className="xl:hidden glass-nav hairline-t" data-testid="nav-mobile-panel">
          <div className="container-luxe py-4 flex flex-col gap-3">
            {links.map((l) => (
              <Link key={l.key} to={l.to} onClick={() => setOpen(false)} className="text-sm py-1 font-display tracking-[0.15em] uppercase text-[var(--ink)]/80" data-testid={`mnav-${l.key}`}>
                {l.label}
              </Link>
            ))}
            <Link to={p("booking")} onClick={() => setOpen(false)} className="btn-gold mt-2" data-testid="mnav-book">
              {t("cta.book_vip")}
            </Link>
            <a
              href={BRAND_INFO.instagram}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setOpen(false)}
              className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-full bg-[var(--gold)] text-white hover:bg-[var(--ink)] transition-colors font-display text-[12px] font-semibold uppercase tracking-[0.16em]"
              data-testid="mnav-instagram"
            >
              <Instagram size={16} /> Instagram
            </a>
            <div className="mt-3 pt-3 hairline-t flex items-center justify-between">
              <span className="text-[10px] uppercase tracking-[0.22em] text-[var(--ink)]/50 font-display">Language</span>
              <div className="flex items-center text-[11px] font-display border border-[var(--ink)]/15 rounded-full overflow-hidden">
                {["fr", "en", "it", "ru"].map((code) => (
                  <button
                    key={code}
                    data-testid={`mlang-${code}`}
                    onClick={() => { switchLang(code); setOpen(false); }}
                    className={`px-3 py-1.5 ${lang === code ? "bg-[var(--ink)] text-[var(--bg-base)]" : "text-[var(--ink)]/60 hover:text-[var(--ink)]"}`}
                  >{code.toUpperCase()}</button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
